import React, { useEffect, useRef, useState } from 'react';
import './styles.scss';
import { ActionButton, Container, Footer, Header, HeroText, MainText, ModalContent } from './styledComponents';
import { Close, House, Warning } from '@mui/icons-material';
import houseTypeServices from 'lib/signtouch/houseTypeServices';
import { FormControl, InputLabel, MenuItem, Select, Skeleton, Tab, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import addPending from 'utils/addPending';
import removePending from 'utils/removePending';
import { addError, addNotification } from 'utils';
import { deleteHouseType } from 'store/actions/houseTypes';
import { setPlots } from 'store/actions/plots';

export const HouseMap = new Map();

const DeleteConfirmation = ({ houseType, closeModal }) => {
  const  {developmentId } = useParams();
  const currentDevelopment = useSelector(
    (state) => state.general.currentDevelopment,
  );
  const houseTypes = useSelector((state) => state.houseTypes); // array of house types
  const dispatch = useDispatch();
  const [activeHouseType, setActiveHouseType] = useState(houseTypes.find(type => type.id === houseType) || null);
  const [itemsToDelete, setItemsToDelete] = useState([]);
  const [action, setAction] = useState('delete'); // 'delete' or 'transfer'
  const [transferTo, setTransferTo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inProcess, setInProcess] = useState(false);
  const [confirmation, setConfirmation] = useState({
    confirm: false,
    deleteHouse: true
  });
  const confirmRef = useRef(null);
  // inital setup to fetch items to delete based on houseType
  const initalization = async () => {
    if (houseType && !HouseMap.has(houseType)) {
      try {
        const items = await houseTypeServices.getItemsToDelete(houseType);
        setItemsToDelete(items);
        setLoading(false);
        HouseMap.set(houseType, items);
      } catch (error) {
        console.error('Error fetching items to delete:', error);
      }
    } else if (HouseMap.has(houseType)) {
      console.log('Using cached items for house type:', houseType);
      setItemsToDelete(HouseMap.get(houseType));
      setLoading(false);
    }
  }
  // transferHouses
  const transferHouses = async () => {
    if (action === 'delete') {
      setConfirmation({ ...confirmation, confirm: false });
      setAction('transfer');
      return;
    }
    if (!confirmation.confirm) {
      if (confirmRef.current) {
        // highlight the checkbox
        confirmRef.current.style.boxShadow = '0 0 5px red';
      }
      addError('Please confirm checkbox for transfer');
      return;
    }
    const pending = addPending(`Transferring...`);
    try {
      setInProcess(true);
      const payload = itemsToDelete.map(item => ({
        plot: item.id,
        transferTo: transferTo,
        development: item.developmentId  || developmentId,
      }));
      await houseTypeServices.transferHouses({ transferList: payload, houseTypeId: houseType, deleteHouse: confirmation.deleteHouse });
      // remove pending
      addNotification('Houses transferred successfully');
      if (confirmation.deleteHouse) {
        const house = houseTypes.find(type => type.id === houseType);
        dispatch(deleteHouseType({...house}));
      }

      if (currentDevelopment.type === 'signtouch-site') {
        dispatch(setPlots());
      }
      
      HouseMap.delete(houseType);
      HouseMap.delete(transferTo);
      
    } catch (error) {
      console.error('Error transferring houses:', error);
    } finally {
      setInProcess(false);
      removePending(pending);
      closeModal();
    }
  }

  const deletePlots = async () => {
    if (!confirmation.confirm) {
      if (confirmRef.current) {
        // highlight the checkbox
        confirmRef.current.style.boxShadow = '0 0 5px red';
      }
      addError('Please confirm checkbox for deletion');
      return;
    }
    const pending = addPending(`Deleting...`);
    try {
      setInProcess(true);
      const payload = itemsToDelete.map(item => ({
        plot: item.id,
        development: item.developmentId || developmentId,
      }));
      await houseTypeServices.deletePlotsBatch({ deleteList: payload , houseTypeId: houseType , deleteHouse: confirmation.deleteHouse });
      addNotification('Deleted successfully');
      if (confirmation.deleteHouse) {
        const house = houseTypes.find(type => type.id === houseType);
        dispatch(deleteHouseType({...house}));
      }
      if (currentDevelopment.type === 'signtouch-site') {
        dispatch(setPlots());
      }
      HouseMap.delete(houseType);
    } catch (error) {
      console.error('Error deleting plots:', error);
      addNotification('Error deleting plots');
    } finally {
      setInProcess(false);
      closeModal();
      removePending(pending);
    }
  }

  useEffect(() => {
    initalization();
  }, [houseType]);

  return (
    <Container>
      <ModalContent>
          <div style={{ display: 'flex', margin: '0', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <HeroText>{activeHouseType ? activeHouseType.name : 'House Type'}</HeroText>
            <Close onClick={closeModal} />
          </div>
        <Header>
          <Warning style={{ fontSize: '8vh', color: 'red', margin: '0 2rem' }} />
          <MainText>
            <h2>Following Items will be affected by this action</h2>
            <p style={{ margin: 0 }}>
              <ul style={{ paddingLeft: '17px' }}>
                <li>Plots can be transferred to another house type</li>
                <li>Otherwise, plots associated with this house type will be deleted</li>
              </ul>
            </p>
          </MainText>
        </Header>
        {action === 'transfer' && (
          // a dropdown to select house type to transfer to
          <FormControl fullWidth>
            <InputLabel id="house-type-select-label">Select House Type</InputLabel>
            <Select
              labelId="house-type-select-label"
              label="Select House Type"
              sx={{ color: 'black' }}
              value={transferTo}
              onChange={(e) => setTransferTo(e.target.value)}
            >
              {houseTypes.map((type) => {
                if (type.id === houseType) return null; // skip the current house type
                return (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                )
              })}
            </Select>
          </FormControl>
        )}
        <TableContainer width="100%" style={{ maxHeight: '30vh', overflowY: 'auto' }}>
          <Table>
            <TableBody>
                {itemsToDelete.length > 0 
                  ?
                  itemsToDelete.map((item, index) => (
                    <TableRow>
                        <TableCell key={index}>
                          Plot {item.number}
                        </TableCell>
                        <TableCell key={`region-${index}`}>
                          {item.regionName ? `${item.regionName} - ` : ''} {item.developmentName ? `${item.developmentName}` : ' - '}
                        </TableCell>
                    </TableRow>
                    ))
                    : <TableCell colSpan={3} style={{ textAlign: 'center', padding: 0 }}>
                      {loading ? 
                        <>
                          <Skeleton variant="rounded" animation="wave" width="100%" height={30} />
                          <Skeleton sx={{ marginTop: '8px' }} variant="rounded" animation="wave" width="100%" height={30} />
                          <Skeleton sx={{ marginTop: '8px' }} variant="rounded" animation="wave" width="100%" height={30} />
                          <Skeleton sx={{ marginTop: '8px' }} variant="rounded" animation="wave" width="100%" height={30} />
                          <Skeleton sx={{ marginTop: '8px' }} variant="rounded" animation="wave" width="100%" height={30} />
                        </>
                        : 'No items to delete'
                    }
                    </TableCell>
                  }
            </TableBody>
          </Table>
        </TableContainer>
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px'}}>
          <input style={{ margin: 0, marginRight: '8px' }}  type="checkbox" checked={confirmation.deleteHouse} onChange={(e) => setConfirmation({ ...confirmation, deleteHouse: e.target.checked })} id="deleteHouse" />
          <p style={{ margin: 0 }}> Delete house type, if unchecked this will delete all plots but not the actual house type.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px'}}>
          <input  style={{ margin: 0, marginRight: '8px' }} ref={confirmRef} checked={confirmation.confirm} type="checkbox" onChange={(e) => setConfirmation({ ...confirmation, confirm: e.target.checked })} id="confirmDeletion" />
          <p style={{ margin: 0 }}> Are you sure you want to {action === 'delete' ? 'delete' : 'transfer'} these plots? This action cannot be undone.</p>
        </div>
        <Footer>
          <ActionButton className='cancel' onClick={() => {
            setConfirmation({ ...confirmation, confirm: false });
            if (action === 'transfer') {
              setAction('delete');
              setTransferTo(null);
            } else {
              closeModal();
            }
          }}>{action === 'transfer' ? 'Cancel' : 'Close'}</ActionButton>
          <div>
            <ActionButton style={{ marginRight: '8px' }} className={`confirm ${loading || inProcess || action !== 'delete' || !confirmation.confirm ? 'disabled' : ''}`} onClick={() => {
              deletePlots();
              }}>Delete</ActionButton>
            <ActionButton className={`transfer ${itemsToDelete.length === 0 || (action === 'transfer' && !transferTo) || loading || inProcess || itemsToDelete.length === 0 ? 'disabled' : ''}`} onClick={() => {
              transferHouses();
            }}>{action === 'transfer' ? 'Submit' : 'Transfer'}</ActionButton>
          </div>
        </Footer>
      </ModalContent>
    </Container>
  )
}


export default DeleteConfirmation;